import SearchIcon from '@mui/icons-material/Search';
import {
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import { GridCellParams, GridColDef, GridPaginationModel, GridRowsProp } from '@mui/x-data-grid';
import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import FormsService from '../../../../../services/forms-service/forms-service';
import { PacketForms, SpecialtyIndexes } from '../../../../../services/forms-service/forms-service-types';
import DataGrid from '../../../../shared/data-grid';
import { FilterBar, FilterDropdown } from './style';

export enum FormType {
  Registration = 0,
  Consent = 2,
  Miscellaneous = 3
  // Uploaded = 4,
}

type FormTabProps = {
  specialties: SpecialtyIndexes[];
  visible: boolean;
  locationId?: number | null;
  selectedForms: PacketForms[];
  setSelectedForms: Dispatch<SetStateAction<PacketForms[]>>;
};

const FormLink: React.FC<GridCellParams> = (props) => {
  return (
    <a target="_blank" href={`/forms-frame/forms/view/${props.id}?forceFrameActivation=true`}>
      {props.value as string}
    </a>
  );
};

const useCallDebounced = (callback: () => void, delay: number) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [callback, delay]);
};

export default function FormsTab({ specialties, visible, locationId, selectedForms, setSelectedForms }: FormTabProps) {
  const [data, setData] = useState([] as GridRowsProp);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');

  const { config } = useConfig();
  const oidc = useOidcWithAdmin();
  const formsService = useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc.oidcService]);

  const callForm = useCallback(async () => {
    try {
      setLoading(true);
      const response = await formsService.listForms({
        search: searchTerm,
        filterByFormType: selectedType,
        filterBySpecialty: selectedSpecialty,
        locationId: locationId ? locationId : undefined,
        page: page
      });
      setData(response.forms);
      setCount(response.count);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [locationId, page, searchTerm, selectedType, selectedSpecialty, formsService]);

  useCallDebounced(callForm, 500);

  const formTypeOptions = () =>
    Object.entries(FormType)
      .filter(([, value]) => !isNaN(Number(value)))
      .map(([key, value]) => ({ value, text: key }));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setSelectedType(event.target.value);
  };

  const handleSpecialtyChange = (event: SelectChangeEvent) => {
    setSelectedSpecialty(event.target.value);
  };

  const customFormName = (params: GridCellParams) => {
    return <FormLink {...params} />;
  };

  const formToggleCheckedAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedForms(() => {
      if (event.target.checked) {
        return data.map((form, index) => ({ formId: form.id, order: index }));
      } else {
        return [];
      }
    });
  };

  const checkedAll = useMemo(() => {
    const selectedFormIds = selectedForms.map((form) => form.formId);
    const dataIds = data.map((form) => form.id);
    return selectedFormIds.length === dataIds.length && selectedFormIds.every((id) => dataIds.includes(id));
  }, [selectedForms, data]);

  const formCheckboxHeader = () => <Checkbox onChange={formToggleCheckedAll} checked={checkedAll} />;

  const formToggleCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedForms((oldForms) => {
      const newForms = oldForms.slice();
      if (event.target.checked) {
        newForms.push({ formId: event.target.value, order: oldForms.length });
      } else {
        const index = oldForms.findIndex((form) => form.formId === event.target.value);
        newForms.splice(index, 1);
      }
      return newForms;
    });
  };

  const formCheckboxCell = (params: GridCellParams) => (
    <div>
      <Checkbox
        checked={!!selectedForms.find((a) => a.formId === params.id) || checkedAll}
        value={params.id}
        onChange={formToggleCheckBox}
      />
    </div>
  );

  const handlePageChange = ({ page }: GridPaginationModel) => {
    setPage(page);
  };

  const formColumns: GridColDef[] = [
    {
      field: 'col1',
      renderHeader: formCheckboxHeader,
      width: 70,
      renderCell: formCheckboxCell,
      sortable: false,
      disableColumnMenu: true
    },
    { field: 'name', headerName: 'Name', flex: 2, sortable: false, filterable: false, renderCell: customFormName },
    { field: 'formTypeName', headerName: 'Type', flex: 1, sortable: false, filterable: false },
    { field: 'specialty', headerName: 'Specialty', flex: 1, sortable: false, filterable: false }
  ];

  if (!visible) {
    return null;
  }

  return (
    <>
      <FilterBar container>
        <Grid item xs={12} sm={6}>
          <TextField
            onChange={handleSearchChange}
            error={searchTerm != '' && searchTerm.length < 3}
            helperText={searchTerm != '' && searchTerm.length < 3 ? 'Search term must be at least 3 characters' : ''}
            value={searchTerm}
            label="Search"
            variant="outlined"
            id="formSearch"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <FilterDropdown item container xs={12} sm={6} spacing={1}>
          <Grid item>
            <FormControl variant="outlined">
              <InputLabel id="formTypesLabel" shrink>
                Type
              </InputLabel>
              <Select
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  }
                }}
                onChange={handleTypeChange}
                label="Type"
                value={selectedType}
                displayEmpty
                autoWidth={false}
                fullWidth
                input={<OutlinedInput notched label="Type" id="formTypesLabel" />}
              >
                <MenuItem value="">All</MenuItem>
                {formTypeOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl variant="outlined">
              <InputLabel id="specialtiesLabel" shrink>
                Specialty
              </InputLabel>
              <Select
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  }
                }}
                onChange={handleSpecialtyChange}
                displayEmpty
                value={selectedSpecialty}
                autoWidth={false}
                fullWidth
                input={<OutlinedInput notched label="Specialty" id="specialtiesLabel" />}
              >
                <MenuItem value="">All</MenuItem>
                {specialties.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </FilterDropdown>
      </FilterBar>
      <DataGrid
        rows={data}
        columns={formColumns}
        pageSizeOptions={[10]}
        paginationModel={{
          page,
          pageSize: 10
        }}
        pagination
        rowCount={count}
        paginationMode="server"
        onPaginationModelChange={handlePageChange}
        autoHeight={true}
        hideMargin
        loading={loading}
        hideFooterSelectedRowCount
        rowHeight={30}
      />
    </>
  );
}
